import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecentlyEnteredMarkets } from '../../api/hooks';
import { gvSharpColor, gvSharpText } from '../GameView/gameViewUtils';
import { marketLabel } from './tradingDashUtils';

function fmtFee(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

function formatMarketSubtitle(row) {
  const parts = [];
  if (row.market_id) {
    const line = row.line != null ? ` ${row.line}` : '';
    parts.push(`${marketLabel(row.market_id)}${line}`);
  }
  if (row.league) parts.push(row.league);
  return parts.length ? parts.join(' · ') : '—';
}

function RecentEntryCard({ row, onClick }) {
  const playerName = row.player_name || '—';
  const subtitle = formatMarketSubtitle(row);
  const sharp = row.avg_fee_weighted_sharp_rate;
  const sharpNum = sharp != null && Number.isFinite(Number(sharp)) ? Number(sharp) : null;
  const feesOver = fmtFee(row.total_fees_over);
  const feesUnder = fmtFee(row.total_fees_under);
  const feesTotal = fmtFee(row.total_fees) || '$0';

  return (
    <button type="button" className="tdash-entries-card" onClick={onClick}>
      <div className="tdash-entries-card-top">
        <span className="tdash-entries-player">{playerName}</span>
        <span className="tdash-entries-count" title={`${row.n_over ?? 0} over · ${row.n_under ?? 0} under`}>
          {row.n_entries ?? 0} ent
        </span>
      </div>
      <div className="tdash-entries-sub" title={row.market_key}>
        {subtitle}
      </div>
      <div className="tdash-entries-card-foot">
        <div className="tdash-entries-fees">
          <span className="tdash-fee-leg tdash-fee-leg--o">
            O {feesOver ?? '—'}
          </span>
          <span className="tdash-fee-leg tdash-fee-leg--u">
            U {feesUnder ?? '—'}
          </span>
          <span className="tdash-fee-total">{feesTotal}</span>
        </div>
        <span
          className="tdash-entries-sharp"
          style={{
            backgroundColor: gvSharpColor(sharpNum) || 'var(--td-panel-2)',
            color: gvSharpText(sharpNum),
          }}
          title="Fee-weighted avg sharp"
        >
          {sharpNum != null ? sharpNum.toFixed(2) : '—'}
        </span>
      </div>
    </button>
  );
}

export default function RecentlyEnteredPanel({
  minutes = 60,
  limit = 50,
  compact = false,
  rows: rowsProp,
  isLoading: isLoadingProp,
}) {
  const navigate = useNavigate();
  const fetchInternally = rowsProp === undefined;
  const { data: fetchedRows = [], isLoading: fetchLoading } = useRecentlyEnteredMarkets(
    { minutes, limit },
    { enabled: fetchInternally }
  );
  const rows = rowsProp ?? fetchedRows;
  const isLoading = isLoadingProp ?? (fetchInternally && fetchLoading);

  const handleMarketClick = useCallback(
    (marketKey) => {
      navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
    },
    [navigate]
  );

  return (
    <section className={`tdash-entries${compact ? ' tdash-entries--compact' : ''}`}>
      <h3 className="tdash-entries-title">
        {compact ? `Recent · ${minutes}m` : `Recently entered (past ${minutes} min)`}
        {rows.length > 0 ? <span className="tdash-entries-count-badge">{rows.length}</span> : null}
      </h3>
      <div className="tdash-entries-table-wrap">
        {isLoading && rows.length === 0 ? (
          <div className="tdash-loading">Loading entries…</div>
        ) : rows.length === 0 ? (
          <div className="tdash-empty">No entries match your filters.</div>
        ) : compact ? (
          <div className="tdash-entries-cards">
            {rows.map((row) => (
              <RecentEntryCard
                key={row.market_key}
                row={row}
                onClick={() => handleMarketClick(row.market_key)}
              />
            ))}
          </div>
        ) : (
          <table className="tdash-entries-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Market</th>
                <th>Fixture</th>
                <th>League</th>
                <th>Entries</th>
                <th>Direction</th>
                <th>Avg sharp</th>
                <th>Total fees</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const playerName = row.player_name || '—';
                const marketLabelText = row.market_id
                  ? `${marketLabel(row.market_id)}${row.line != null ? ` ${row.line}` : ''}`
                  : '—';
                const fixtureLabel =
                  row.fixture_home_team && row.fixture_away_team
                    ? `${row.fixture_home_team} v ${row.fixture_away_team}`
                    : row.fixture_start_time != null && row.fixture_start_time > 0
                      ? new Date(row.fixture_start_time * 1000).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—';
                return (
                  <tr
                    key={row.market_key}
                    onClick={() => handleMarketClick(row.market_key)}
                    className="tdash-entries-row"
                  >
                    <td title={row.team_name || ''}>{playerName}</td>
                    <td title={row.market_key}>{marketLabelText}</td>
                    <td title={row.market_key}>{fixtureLabel}</td>
                    <td>{row.league || '—'}</td>
                    <td>{row.n_entries}</td>
                    <td>
                      {row.n_over != null || row.n_under != null
                        ? [
                            row.n_over > 0 && `${row.n_over} over`,
                            row.n_under > 0 && `${row.n_under} under`,
                          ]
                            .filter(Boolean)
                            .join(' / ')
                        : '—'}
                    </td>
                    <td>
                      {row.avg_fee_weighted_sharp_rate != null
                        ? Number(row.avg_fee_weighted_sharp_rate).toFixed(2)
                        : '—'}
                    </td>
                    <td>
                      <strong>${Number(row.total_fees || 0).toFixed(0)}</strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
